/**
 * QiltRadarChart — 5-focus-area QILT Student Experience Survey
 * radar chart for Australian universities. AU cousin of the UK NSS
 * radar. Positivity % axis with the national QILT SES benchmark
 * line for comparison.
 *
 * Source: /api/methodology/qilt-ses/2024/provider?uni_id=X
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";

export default function QiltRadarChart({ uniId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/qilt-ses/2024/provider?uni_id=${uniId}`)
       .then((r) => setData(r.data))
       .catch(() => setData(null));
  }, [uniId]);

  if (!data) return null;
  const themes = (data.themes || []).filter((t) => t.positivity_pct != null);
  if (themes.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-600" data-testid="qilt-radar-empty">
        QILT SES 2024 data isn't published for this university.
      </div>
    );
  }

  const chartData = themes.map((t) => ({
    theme: t.label,
    "This uni":         Math.round(t.positivity_pct * 10) / 10,
    "AU national avg":  t.benchmark_pct != null ? Math.round(t.benchmark_pct * 10) / 10 : null,
  }));

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="qilt-radar-chart">
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] uppercase tracking-wider font-mono">
          QILT verified
        </span>
        <span className="label-uppercase text-emerald-900">
          QILT SES 2024 · student experience map
        </span>
      </div>
      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <RadarChart data={chartData} outerRadius="72%">
            <PolarGrid stroke="#e7e5e4" />
            <PolarAngleAxis dataKey="theme"
                            tick={{ fontSize: 11, fill: "#4C1D95", fontFamily: "monospace" }} />
            <PolarRadiusAxis angle={90} domain={[50, 100]}
                             tick={{ fontSize: 9, fill: "#a8a29e" }} />
            <Radar name="This uni" dataKey="This uni"
                   stroke="#4C1D95" fill="#4C1D95" fillOpacity={0.35} />
            <Radar name="AU national avg" dataKey="AU national avg"
                   stroke="#059669" fill="#059669" fillOpacity={0.15} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-[11px] text-stone-500 leading-snug">
        Purple = this uni. Green = QILT SES 2024 national average.
        Radius shows positivity %; farther out = better.
        {data.n_responses ? ` Based on ${Number(data.n_responses).toLocaleString()} student responses.` : ""}
        {" "}Source · qilt.edu.au 2024 institution report.
      </p>
    </div>
  );
}
